import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { GET_TESTER_IDS, GET_TESTERS, TesterItem } from './Testers.queries';
import TableWindow from '../../components/Table/TableWindow';
import useTablePages from '../../hooks/useTablePages';
import availableColumns from './Testers.columns';
import { useOrderBy } from '../../hooks/useOrderBy';
import PageBar from '../../components/PageBar/PageBar';
import { PortalWithState } from 'react-portal';
import Button from '../../components/Button/Button';
import TesterEditModal from '../../components/Tester/TesterEditModal';
import { RouteComponentProps, withRouter } from 'react-router';
import useThirdPaneCallbacks from '../../hooks/useThirdPaneCallbacks';
import TesterThirdPane from '../../components/Tester/TesterThirdPane';
import ThirdPaneLayout, {
  MainColumn,
} from '../../components/Layout/ThirdPaneLayout';
import FilterBarTester from './FilterBarTester';
import useMode from '../../hooks/useMode';
import useColumnConfiguration from '../../hooks/useColumnConfiguration';
import { useHistory } from 'react-router-dom';
import { TesterStatus } from '../../components/Tester/TesterStatusPicker';
import { ProfileType } from '../../components/Tester/ProfileTypePicker';
import RequirePerms from '@/components/RequirePerms';

type TestersFilters = {
  status?: TesterStatus;
  'profile.type'?: ProfileType;
} & Record<string, any>;

type TesterDetailParams = {
  id?: string;
};

type TesterDetailProps = RouteComponentProps<TesterDetailParams>;

/**
 * @component for testers page
 */
function Testers({ match }: TesterDetailProps): JSX.Element {
  const [filters, setFilters] = useState<TestersFilters | null>({
    status: 'ACTIVE',
  });

  const { orderBy, onHeaderClick } = useOrderBy(
    'testers',
    availableColumns,
    'profile.name'
  );

  const mode = useMode();
  const history = useHistory();

  const {
    loading,
    error,
    itemIds,
    selectedIds,
    setSelectedIds,
    itemLoader,
    refetch,
  } = useTablePages<TesterItem>(
    'vendors',
    GET_TESTER_IDS,
    {
      where: filters,
      limit: null,
      orderBy,
    },
    GET_TESTERS,
    (ids) => ({
      where: {
        id: ids,
      },
      limit: null,
      orderBy,
    })
  );

  const { visibleColumns, columnConfiguration, setColumnConfiguration } =
    useColumnConfiguration(availableColumns, 'testers');

  const { closeThirdPane, onRowClick, prev, prevEnabled, next, nextEnabled } =
    useThirdPaneCallbacks(`/${useMode()}/testers`, itemIds, match.params.id);

  return (
    <ThirdPaneLayout paneWidth={550} paneVisible={!!match.params.id}>
      <MainColumn px="mainContent" pb="mainContent">
        <PageBar title="Testers">
          <RequirePerms vendors="CREATE">
            <PortalWithState closeOnEsc>
              {({ openPortal, closePortal, portal }) => (
                <>
                  <Button
                    ml={3}
                    variant="primary"
                    icon="plus"
                    onClick={openPortal}
                  >
                    Add Backflow Tester
                  </Button>
                  {portal(
                    <TesterEditModal
                      action="add"
                      onClose={closePortal}
                      onSave={(tester) => {
                        history.push(`/${mode}/testers/${tester.id}`);
                        refetch();
                      }}
                    />
                  )}
                </>
              )}
            </PortalWithState>
          </RequirePerms>
        </PageBar>
        <FilterBarTester
          filters={filters ?? {}}
          setFilters={setFilters}
          selectedId={match.params.id}
        />
        {loading && itemIds.length === 0 ? (
          'Loading...'
        ) : (
          <TableWindow
            columns={visibleColumns}
            itemIds={itemIds}
            itemLoader={itemLoader}
            onSelectionChange={setSelectedIds}
            selectedIds={selectedIds}
            activeIds={match.params.id ? [match.params.id] : undefined}
            onHeaderClick={onHeaderClick}
            orderBy={orderBy}
            onRowClick={onRowClick}
            columnConfiguration={columnConfiguration}
            setColumnConfiguration={setColumnConfiguration}
          />
        )}
      </MainColumn>
      <TesterThirdPane
        vendorId={match.params.id}
        onPrev={prev}
        prevEnabled={prevEnabled}
        onNext={next}
        nextEnabled={nextEnabled}
        onToggle={closeThirdPane}
      />
    </ThirdPaneLayout>
  );
}

Testers.propTypes = {
  match: PropTypes.any,
};

export default withRouter(Testers);
